import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { useTeamStore } from '../store/teamStore';
import { useAuthStore } from '../store/authStore';
import { NoTeamsGate } from './ui/NoTeamsGate';

// Custom hooks - using named imports
import { usePractices } from '../hooks/usePractices';
import { useDrills } from '../hooks/useDrills';
import { useTimers } from '../hooks/useTimers';

// Components
import PracticeForm from './practice/PracticeForm';
import PracticeList from './practice/PracticeList';
import PracticeHeader from './practice/PracticeHeader';
import DrillList from './practice/DrillList';
import MobileUtilityNav from './practice/MobileUtilityNav';
import DrillForm from './DrillForm';
import PageHeader from './layout/PageHeader';
import UpgradeModal from './UpgradeModal';
import { ConfirmModal } from './ConfirmModal';

// Types
import type { Drill, Practice } from '../types';
import type { PracticePlanRequest } from '../services/aiService.types';

// Services
import { saveDrills, addSingleDrill, updateSingleDrill } from '../services/practice/practiceService';
import { generatePracticePlan } from '../services/aiService.simplified';
import { FavoriteService } from '../services/favoriteService';
import { subscriptionService } from '../services/subscriptionService';
import { practicePlanJobService } from '../services/practicePlanJobService';

// Subscription
import { useFeatureAccess } from '../hooks/useFeatureAccess';

// Utils
import { printPracticeSchedule } from '../utils/printUtils';
import { getFocusAreasForTheme } from '../utils/sportUtils';


// Icons
import { Plus } from 'lucide-react';
import WhistleIcon from './icons/WhistleIcon';

// Global cache to track if favorite_drill_steps table exists - prevents repeated DB calls
const GLOBAL_CACHE = {
  favoriteTableExists: false,
  favoriteTableChecked: false
};

type ConfirmDeleteState =
  | { type: 'practice'; id: string }
  | { type: 'drill'; id: string; name?: string }
  | { type: 'position_group'; id: string; groupName: string }
  | null;

export function PracticeManagement() {
  // Get team from store - this is what's updated when switching teams
  const { currentTeam } = useTeamStore();
  const { allTeams, fetchAllTeams } = useTeamStore();
  const { user } = useAuthStore();
  const [confirmDelete, setConfirmDelete] = useState<ConfirmDeleteState>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  // Pro feature access
  const { checkFeatureAccess, showUpgradeModal, handleUpgrade, handleCloseModal } = useFeatureAccess();
  
  // UI state
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>(
    localStorage.getItem('timeFormat') === '24h' ? '24h' : '12h'
  );
  
  // Simpler approach for favorites - use ref instead of state to prevent re-renders
  const [favoriteStepIds, setFavoriteStepIds] = useState<string[]>([]);
  const favoriteTableCheckedRef = useRef(GLOBAL_CACHE.favoriteTableChecked);
  
  // AI Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPlan, setGeneratedPlan] = useState<any>(null);
  const [activePlanJobId, setActivePlanJobId] = useState<string | null>(null);
  
  // Track current team ID to detect changes
  const lastTeamIdRef = useRef<string | null>(null);
  
  // Track temporary drills for auto-save functionality
  const tempDrillsRef = useRef<Drill[]>([]);
  const practiceIdRef = useRef<string | null>(null);

  // Synchronous re-entrancy guard for handleGeneratePlan: `isGenerating` state only
  // disables the button after a re-render, leaving a window where a double-click fires
  // the handler twice before React catches up. A ref-based flag closes that race exactly.
  const isGeneratingRef = useRef(false);
  
  // Get practice management hooks
  const {
    practices,
    selectedPractice,
    isLoading: isPracticesLoading,
    isAddingPractice,
    editingPractice,
    fetchPractices,
    deletePractice,
    selectPractice,
    selectNewPractice,
    startEditingPractice,
    startAddingPractice,
    handlePracticeFormClose
  } = usePractices({ currentTeam });
  
  // Get drill management hooks
  const {
    drills,
    isLoading: isDrillsLoading,
    isAddingDrill,
    editingDrill,
    coaches,
    drillCoaches,
    fetchDrills,
    fetchCoaches,
    handleCoachChange,
    deleteDrill,
    deletePositionGroup,
    saveDrillOrder,
    clearDrills,
    startEditingDrill,
    startAddingDrill,
    handleDrillFormClose,
    setDrills
  } = useDrills({ selectedPractice });
  
  // Get timer hooks and properly use the destructured variables
  const {
    currentDrillIndex,
    warningDrillIndex
  } = useTimers({ drills, selectedPractice });

  // Track team changes
  useEffect(() => {
    if (currentTeam?.id !== lastTeamIdRef.current) {
      // Update our team reference
      lastTeamIdRef.current = currentTeam?.id || null;
    }
  }, [currentTeam]);
  
  // Listen for team change and cache clear events
  useEffect(() => {
    const handleTeamChanged = (e: Event) => {
      const customEvent = e as CustomEvent;
      const newTeamId = customEvent.detail?.teamId;
      
      // Clear any temporary drills and state when team changes
      tempDrillsRef.current = [];
      practiceIdRef.current = null;
      setGeneratedPlan(null);
      
      // Force refetch of practices for the new team
      if (newTeamId) {
        // Add a small delay to ensure the team context has updated
        setTimeout(() => {
      fetchPractices();
        }, 100);
      }
    };
    
    const handleCacheClear = (e: Event) => {
      const customEvent = e as CustomEvent;
      
      // Clear all temporary state immediately
      tempDrillsRef.current = [];
      practiceIdRef.current = null;
      setGeneratedPlan(null);
    };
    
    window.addEventListener('team-changed', handleTeamChanged);
    window.addEventListener('clear-team-cache', handleCacheClear);
    
    return () => {
      window.removeEventListener('team-changed', handleTeamChanged);
      window.removeEventListener('clear-team-cache', handleCacheClear);
    };
  }, [fetchPractices]);
  
  // Handle window resize for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch practices when component mounts or team changes - use a ref to prevent duplicate fetches
  useEffect(() => {
    let mounted = true;
    
    if (currentTeam?.id && mounted) {
      // If team has changed, force a refresh
      if (lastTeamIdRef.current !== currentTeam.id) {
        fetchPractices();
      } else {
        fetchPractices();
      }
    }
    
    return () => {
      mounted = false;
    };
  }, [currentTeam?.id]); // Intentionally exclude fetchPractices to avoid infinite loop
  
  // One-time check for favorite_drill_steps table
  useEffect(() => {
    // Skip if we've already checked
    if (favoriteTableCheckedRef.current) return;
    
    const checkFavoritesTable = async () => {
      try {
        // Force enable favorites for now (bypass table check)
        GLOBAL_CACHE.favoriteTableExists = true;
        
        try {
          const userId = await FavoriteService.getCurrentUserId();
          const teamId = currentTeam?.id;
          const favoriteIds = teamId ? await FavoriteService.getFavoriteStepIds(userId, teamId) : [];
          setFavoriteStepIds(favoriteIds);
        } catch (loadError) {
          console.error('Error loading favorites:', loadError);
        }
      } catch (e) {
        console.error('Error checking favorites table:', e);
        // Don't disable favorites due to this error
        GLOBAL_CACHE.favoriteTableExists = true;
      } finally {
        // Mark as checked in both ref and global cache
        favoriteTableCheckedRef.current = true;
        GLOBAL_CACHE.favoriteTableChecked = true;
      }
    };
    
    checkFavoritesTable();
  }, []); // Empty dependency array - run once on mount
  
  // Load coaches when team changes - memoized to prevent re-renders
  useEffect(() => {
    if (currentTeam?.id) {
      fetchCoaches(currentTeam.id);
    }
  }, [currentTeam?.id]); // Intentionally exclude fetchCoaches to avoid infinite loop
  
  // Toggle time format between 12h and 24h - memoized to prevent re-renders
  const handleToggleTimeFormat = useCallback(() => {
    setTimeFormat((prev) => {
      const newFormat = prev === '12h' ? '24h' : '12h';
      localStorage.setItem('timeFormat', newFormat);
      return newFormat;
    });
  }, []);
  
  // Toggle favorite status for a drill step using the centralized service
  const handleToggleFavorite = useCallback(async (stepId: string) => {
    try {
      const userId = await FavoriteService.getCurrentUserId();
      const teamId = currentTeam?.id;
      if (!teamId) {
        toast.error('No team selected');
        return;
      }
      const isNowFavorited = await FavoriteService.toggleFavoriteStep(userId, teamId, stepId);
      
      // Update local state
      if (isNowFavorited) {
        setFavoriteStepIds(prev => [...prev, stepId]);
        toast.success('Added to favorites');
      } else {
        setFavoriteStepIds(prev => prev.filter(id => id !== stepId));
        toast.success('Removed from favorites');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorites. Please try again.');
    }
  }, [currentTeam?.id]);
  
  // Print practice schedule - memoized to prevent re-renders
  const handlePrint = useCallback(() => {
    if (!selectedPractice || drills.length === 0) {
      toast.error('No practice or drills to print');
      return;
    }

    printPracticeSchedule(selectedPractice, drills, coaches, drillCoaches, timeFormat);
  }, [selectedPractice, drills, coaches, drillCoaches, timeFormat]);
  
  // Generate practice plan - memoized to prevent re-renders
  const handleGeneratePlan = useCallback(async () => {
    // Check Pro access for AI practice generation
    if (!checkFeatureAccess('AI-powered practice planning')) {
      return;
    }
    
    if (!currentTeam || !selectedPractice) {
      toast.error('Please select a team and practice first');
      return;
    }

    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    setIsGenerating(true);
    setActivePlanJobId(null);

    try {
      // Calculate practice duration in minutes
      const [startHours, startMinutes] = selectedPractice.start_time.split(':').map(Number);
      const [endHours, endMinutes] = selectedPractice.end_time.split(':').map(Number);
      
      let totalMinutes = (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes);
      // Handle cases where practice ends next day
      if (totalMinutes <= 0) {
        totalMinutes += 24 * 60;
      }
      
      // Create the AI service request (different format from the practice service)
      const aiRequest: PracticePlanRequest = {
        team: currentTeam,
        duration: totalMinutes,
        desiredDrillStepsPerDrill: Math.min(5, Math.max(1, currentTeam.defaultDrillStepsPerDrill ?? 3)),
        usePositionGroups: currentTeam.aiUsePositionGroups ?? true,
        theme: selectedPractice.theme ?? 'Team Day',
        focusAreas: getFocusAreasForTheme(selectedPractice.theme),
        date: selectedPractice.date,
        startTime: selectedPractice.start_time,
        location: selectedPractice.location,
        ageLevel: currentTeam.ageLevel,
        experienceLevel: currentTeam.experienceLevel
      };

      // Create a server-side job first so the user can safely resume after refresh.
      // idempotencyKey is generated fresh per button press (before any async work) so a
      // duplicate submission of the SAME press reuses it; a genuinely new press gets a
      // new key and is never deduplicated against an earlier, intentional generation.
      const { jobId } = await practicePlanJobService.enqueue({
        practiceId: selectedPractice.id,
        teamId: currentTeam.id,
        request: aiRequest,
        idempotencyKey: globalThis.crypto?.randomUUID?.() ?? `pp_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      });
      setActivePlanJobId(jobId);
      
      // Show initial toast
      const initialToastId = toast.loading('Generating practice plan...', {
        duration: Infinity,
        position: 'bottom-right',
        style: {
          width: isMobileView ? '100%' : 'auto',
          maxWidth: isMobileView ? '100%' : '400px',
          margin: isMobileView ? '0' : 'auto',
          borderRadius: isMobileView ? '0' : '8px',
          background: '#333',
          color: '#fff'
        }
      });
      
      // Track drills for completion
      let totalDrills = 0;
      let completedDrills = 0;
      const collectedDrills: Omit<Drill, 'id'>[] = [];
      
      const planToDrills = (planLike: any): Drill[] => {
        const planDrills = Array.isArray(planLike?.drills) ? planLike.drills : [];
        return planDrills.map((d: any, index: number) => ({
          id: `generated-${index}`,
          name: d.name,
          duration: d.duration,
          description: d.description || '',
          focus_areas: [d.focus_area || 'Team'],
          intensity_level: d.intensity_level ?? null,
          position_groups: (d.position_groups || []).map((pg: any) => ({
            group_name: pg.group_name,
            steps: (pg.steps || []).map((s: any, sIndex: number) => ({
              id: `generated-${index}-${pg.group_name}-${sIndex}`,
              name: s.name,
              setup: s.setup || '',
              execution: s.execution || '',
              coaching_points: s.coachingPoints || s.coaching_points || [],
              equipment: s.equipment || '',
              video_url: s.video_url || '',
              description: '',
              duration: 0,
              order_index: sIndex
            }))
          })),
          practice_id: selectedPractice.id
        })) as any;
      };
      
      // Kick off the server-side generator. The function updates job progress as it runs
      // (and continues in the background even if the user navigates away).
      toast.dismiss(initialToastId);
      const FUN_GENERATION_MESSAGES = [
        "Generating your practice so you don't have to.",
        "Go be a human for a minute — I’ve got the clipboard now.",
        "Buying you time for family, snacks, and sanity. Building your practice plan…",
        "Coaches coach. Computers plan. I’m on planning duty.",
        "Turning 'I need a plan' into 'I’m ready to coach'…",
        "Stealing back your evening, one drill at a time…",
        "Putting the drills together while you pretend you’re not thinking about drills.",
        "Building a practice plan that saves you time (and maybe your voice).",
        "Your future self says thanks. Generating your practice…",
        "Drafting a practice plan so you can focus on the fun part: coaching."
      ];
      const pickMessage = (last?: string) => {
        if (FUN_GENERATION_MESSAGES.length === 0) return "Generating practice plan…";
        if (FUN_GENERATION_MESSAGES.length === 1) return FUN_GENERATION_MESSAGES[0];
        let next = last;
        while (next === last) {
          next = FUN_GENERATION_MESSAGES[Math.floor(Math.random() * FUN_GENERATION_MESSAGES.length)];
        }
        return next!;
      };

      const activeMessage = pickMessage();
      toast.loading(activeMessage, { id: 'practice-plan-job', duration: Infinity, position: 'bottom-right' });
      
      let job = await practicePlanJobService.status(jobId);
      // Poll until done
      while (job.status === 'queued' || job.status === 'running') {
        const done = job.progress_done ?? 0;
        const total = job.progress_total ?? 0;
        const suffix = total > 0 ? ` (${done}/${total})` : '';
        const currentDrill = (job as any)?.result?.meta?.current_drill as string | undefined;
        const drillSuffix = currentDrill ? ` — ${currentDrill}` : '';
        const partialPlan = (job as any)?.result ?? null;
        if (partialPlan?.drills && Array.isArray(partialPlan.drills) && partialPlan.drills.length > 0) {
          setGeneratedPlan(partialPlan);
          // Paint generated drills immediately (without waiting for DB save)
          setDrills(planToDrills(partialPlan) as any);
        }
        toast.loading(`${activeMessage}${suffix}${drillSuffix}`, { id: 'practice-plan-job', duration: Infinity, position: 'bottom-right' });
        await new Promise((r) => setTimeout(r, 2000));
        job = await practicePlanJobService.status(jobId);
      }
      
      toast.dismiss('practice-plan-job');

      const plan = (job.result as any) || null;
      if (!plan?.drills || !Array.isArray(plan.drills) || plan.drills.length === 0) {
        throw new Error(job.error || 'No drills were generated');
      }
      
      // Update state with the generated plan
      setGeneratedPlan(plan);
      
      // Show intermediate success message
      const generatedCount = Array.isArray(plan?.drills) ? plan.drills.length : 0;
      toast.success(`Generated ${generatedCount} drills! Now saving to database...`, {
        duration: 3000,
        position: 'bottom-right'
      });
      
      // Small delay to let user see the generated drills before saving
      await new Promise(resolve => setTimeout(resolve, 1000));

      // If the edge function already persisted drills to the DB, just refresh and finish.
      if ((plan as any)?.meta?.persisted_to_db) {
        toast.dismiss('saving-drills');
        await fetchDrills(selectedPractice.id);
        toast.success(`Practice plan generated! ${plan.drills.length} drills saved.`, {
          duration: 5000,
          position: 'bottom-right'
        });

        // Increment practice generation count for rookie users
        if (user?.id) {
          try {
            await subscriptionService.incrementUsageCounter('practice_generation_count');
          } catch (error) {
            console.error('Error incrementing practice generation count:', error);
            // Don't show error to user as the main operation succeeded
          }
        }
        return;
      }
      
      // NOTE: For now we keep the existing DB save flow by reusing the client-side converters.
      // TODO in this ticket: have the edge function persist drills directly for true background completion.
      const collectedFromJob: Omit<Drill, 'id'>[] = plan.drills.map((d: any) => ({
        name: d.name,
        duration: d.duration,
        description: d.description || '',
        focus_areas: [d.focus_area || 'Team'],
        intensity_level: d.intensity_level ?? null,
        position_groups: (d.position_groups || []).map((pg: any) => ({
          group_name: pg.group_name,
          steps: (pg.steps || []).map((s: any) => ({
            name: s.name,
            setup: s.setup || '',
            execution: s.execution || '',
            coaching_points: s.coachingPoints || s.coaching_points || [],
            equipment: s.equipment || '',
            video_url: s.video_url || '',
            description: ''
          }))
        })),
        practice_id: selectedPractice.id
      }));

      if (collectedFromJob.length > 0) {
        toast.loading(`Saving ${collectedFromJob.length} drills...`, {
          id: 'saving-drills',
          position: 'bottom-right'
        });
        
        try {
          // Water breaks are already added by the AI service during generation
          await saveDrills(selectedPractice.id, collectedFromJob as any);
          toast.dismiss('saving-drills');
          toast.success(`Practice plan generated! ${collectedFromJob.length} drills saved (including water breaks).`, {
        duration: 5000,
        position: 'bottom-right',
        style: {
          width: isMobileView ? '100%' : 'auto',
          maxWidth: isMobileView ? '100%' : '400px',
          margin: isMobileView ? '0' : 'auto',
          borderRadius: isMobileView ? '0' : '8px'
        }
      });
          
          // Increment practice generation count for rookie users
          if (user?.id) {
            try {
              await subscriptionService.incrementUsageCounter('practice_generation_count');
            } catch (error) {
              console.error('Error incrementing practice generation count:', error);
              // Don't show error to user as the main operation succeeded
            }
          }
          
          // Clear temporary drill tracking after successful save
          tempDrillsRef.current = [];
          practiceIdRef.current = null;
          
          // Refresh drills to replace temporary ones with real database records
          await fetchDrills(selectedPractice.id);
        } catch (error) {
          toast.dismiss('saving-drills');
          console.error('Error saving drills:', error);
          toast.error('Failed to save drills. Please try again.');
          
          // Clear the temporary drills since save failed
          setDrills([]);
        }
      } else {
        toast.error('No drills were generated. Please try again.');
      }
      
    } catch (error) {
      console.error('Error generating practice plan:', error);
      toast.error('Failed to generate practice plan. Please try again.');
    } finally {
      isGeneratingRef.current = false;
      setIsGenerating(false);
    }
  }, [currentTeam, selectedPractice, isMobileView, fetchDrills, checkFeatureAccess]);

  // Save an individual drill - memoized to prevent re-renders
  const saveDrill = useCallback(async (drill: Omit<Drill, 'id'>, practiceId: string) => {
    try {
      await saveDrills(practiceId, [drill as any]);
    } catch (error) {
      console.error('Error saving drill:', error);
      toast.error(`Failed to save drill: ${drill.name}`);
    }
  }, []);
  
  // Handle practice selection - memoized to prevent re-renders
  const handlePracticeSelect = useCallback((practice: Practice) => {
    selectPractice(practice);
  }, [selectPractice]);
  
  // Request delete (opens confirm modal); actual delete runs on confirm
  const requestDeletePractice = useCallback((practiceId: string) => {
    setConfirmDelete({ type: 'practice', id: practiceId });
  }, []);

  const handleDeleteDrill = useCallback((drillId: string, drillName?: string) => {
    setConfirmDelete({ type: 'drill', id: drillId, name: drillName });
  }, []);

  const handleDeletePositionGroup = useCallback((drillId: string, groupName: string) => {
    setConfirmDelete({ type: 'position_group', id: drillId, groupName });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmDelete) return;
    setIsConfirming(true);
    try {
      if (confirmDelete.type === 'practice') {
        await deletePractice(confirmDelete.id);
      } else if (confirmDelete.type === 'drill') {
        await deleteDrill(confirmDelete.id);
      } else {
        await deletePositionGroup(confirmDelete.id, confirmDelete.groupName);
      }
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error deleting:', error);
    } finally {
      setIsConfirming(false);
    }
  }, [confirmDelete, deletePractice, deleteDrill, deletePositionGroup]);
  
  // Is loading anything?
  const isLoading = isPracticesLoading || isDrillsLoading;
  
  // Load teams on component mount to handle cross-domain localStorage issues
  useEffect(() => {
    const loadTeams = async () => {
      setIsLoadingTeams(true);
      await fetchAllTeams();
      setIsLoadingTeams(false);
    };

    loadTeams();
  }, [fetchAllTeams]);

  // Auto-save functionality to protect against page navigation
  useEffect(() => {
    // Removed the beforeunload event handler to prevent duplicate prompts
    // The toast confirmation in useDrills.tsx provides a better user experience
  }, []);
  
  // Periodic auto-save of temporary drills
  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      const tempDrills = tempDrillsRef.current;
      const practiceId = practiceIdRef.current;
      
      if (tempDrills.length > 0 && practiceId && !isGenerating) {
        (async () => {
          try {
            await saveDrills(practiceId, tempDrills as any);
            
            // Clear temporary drills after successful save
            tempDrillsRef.current = [];
            practiceIdRef.current = null;
            
            // Refresh drills to show saved versions
            await fetchDrills(practiceId);
            
            toast.success('Auto-saved practice drills!', { duration: 2000 });
          } catch (error) {
            console.error('🔄 [Auto-save] Auto-save failed:', error);
          }
        })();
      }
    }, 30000); // Auto-save every 30 seconds
    
    return () => clearInterval(autoSaveInterval);
  }, [isGenerating, fetchDrills]);

  // Show loading state while teams are being loaded
  if (isLoadingTeams) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="caption">Loading teams...</p>
      </div>
    );
  }

  // Show team setup message only after teams are loaded and there are no teams
  if (allTeams.length === 0) {
    return <NoTeamsGate />;
  }

  // Show loading state for team data
  if (!currentTeam) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="caption">Loading team data...</p>
      </div>
    );
  }

  // Mobile view shows either practices or drills, not both
  return (
    <div className="container mx-auto px-4 py-4">
      
      {/* Remove team selector and only show a message if no team is selected */}
      {!currentTeam ? (
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <h3 className="text-lg font-semibold mb-2">No Team Selected</h3>
          <p className="text-gray-600">
            Please select a team from the dashboard or settings page before viewing practices.
          </p>
        </div>
      ) : (
        <>
          {/* Mobile practice header back button */}
          {isMobileView && selectedPractice && (
            <PracticeHeader
              practice={selectedPractice}
              isMobileView={isMobileView}
              timeFormat={timeFormat}
              onBackToPractices={() => selectPractice(null)}
            />
          )}
          
          <div className="flex flex-col md:flex-row gap-6">
            {/* Left column - Practices List (hidden on mobile when practice is selected) */}
            <div className={`md:w-1/3 ${isMobileView && selectedPractice ? 'hidden' : 'block'}`}>
              <PageHeader
                title="Practices"
                icon={<WhistleIcon size={24} />}
                actions={
                <button
                  onClick={startAddingPractice}
                    className="btn-primary flex items-center gap-2"
                  disabled={!currentTeam}
                >
                    <Plus className="h-4 w-4 mr-1" />
                    Create New
                </button>
                }
              />
              
              {isLoading ? (
                <div className="text-center py-8">Loading practices...</div>
              ) : (
                <PracticeList
                  practices={practices}
                  selectedPracticeId={selectedPractice?.id || null}
                  onPracticeSelect={handlePracticeSelect}
                  onEditPractice={startEditingPractice}
                  onDeletePractice={requestDeletePractice}
                  onAddPractice={startAddingPractice}
                  isMobileView={isMobileView}
                  timeFormat={timeFormat}
                  currentTeam={currentTeam}
                  onRefresh={fetchPractices}
                />
              )}
            </div>
            
            {/* Right column - Drills (hidden on mobile when no practice is selected) */}
            <div className={`md:w-2/3 ${isMobileView && !selectedPractice ? 'hidden' : 'block'}`}>
              {!isMobileView && selectedPractice && (
                <PracticeHeader
                  practice={selectedPractice}
                  isMobileView={isMobileView}
                  timeFormat={timeFormat}
                  onBackToPractices={() => {}}
                />
              )}
              
              {selectedPractice ? (
                <DrillList
                  drills={drills}
                  coaches={coaches}
                  drillCoaches={drillCoaches}
                  selectedPractice={selectedPractice}
                  favoriteStepIds={favoriteStepIds}
                  isMobileView={isMobileView}
                  timeFormat={timeFormat}
                  isGenerating={isGenerating}
                  currentTeam={currentTeam}
                  currentDrillIndex={currentDrillIndex}
                  warningDrillIndex={warningDrillIndex}
                  onAddDrill={startAddingDrill}
                  onEditDrill={startEditingDrill}
                  onDeleteDrill={handleDeleteDrill}
                  onDeletePositionGroup={handleDeletePositionGroup}
                  onToggleFavorite={handleToggleFavorite}
                  onPrint={handlePrint}
                  onToggleTimeFormat={handleToggleTimeFormat}
                  onGeneratePlan={handleGeneratePlan}
                  saveDrillOrder={saveDrillOrder}
                  onCoachChange={handleCoachChange}
                  onClearDrills={clearDrills}
                  isLoading={isDrillsLoading}
                />
              ) : !isMobileView ? (
                <div className="text-center py-12 bg-white rounded-lg shadow-md">
                  <h3 className="text-xl font-semibold mb-2">Select a Practice</h3>
                  <p className="text-gray-600 mb-4">
                    Choose a practice from the list to view and manage its drills.
                  </p>
                  {practices.length > 0 && (
                    <p className="text-sm text-gray-500">
                      {practices.length} practice{practices.length !== 1 ? 's' : ''} available (most recent first)
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {/* Practice form modal */}
          {isAddingPractice && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <PracticeForm
                onClose={handlePracticeFormClose}
                editingPractice={editingPractice}
                currentTeam={currentTeam}
                onSuccess={(practice) => {
                  if (practice) {
                    // Select the newly created practice and navigate to it
                    selectNewPractice(practice);
                  }
                }}
              />
            </div>
          )}

          {/* Drill form modal */}
          {isAddingDrill && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <DrillForm
                onClose={handleDrillFormClose}
                onSave={async (drillData) => {
                                      if (selectedPractice?.id) {
                      try {
                        if (editingDrill?.id) {
                          // If we're editing a drill, use the new updateSingleDrill function
                          await updateSingleDrill({
                            ...drillData,
                            id: editingDrill.id,
                            practice_id: selectedPractice.id
                          } as Drill);
                        } else {
                          // If we're adding a new drill, use the new addSingleDrill function
                          await addSingleDrill(selectedPractice.id, drillData as Omit<Drill, 'id'>);
                        }
                        // Refresh the drills list
                        await fetchDrills(selectedPractice.id);
                        toast.success(editingDrill ? 'Drill updated successfully' : 'Drill added successfully');
                      } catch (error) {
                        console.error('Error saving drill:', error);
                        toast.error('Failed to save drill');
                      }
                    }
                }}
                editingDrill={editingDrill}
              />
            </div>
          )}

          {/* Mobile utility navigation */}
          <MobileUtilityNav
            selectedPractice={selectedPractice}
            isMobileView={isMobileView}
            isGenerating={isGenerating}
            onGeneratePlan={handleGeneratePlan}
            onAddDrill={startAddingDrill}
            onClearDrills={clearDrills}
            onToggleTimeFormat={handleToggleTimeFormat}
            onPrint={handlePrint}
            onAddPractice={startAddingPractice}
          />
          
          {/* Upgrade Modal */}
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={handleCloseModal}
            onUpgrade={handleUpgrade}
          />

          {/* Delete confirmation modal */}
          <ConfirmModal
            isOpen={confirmDelete !== null}
            onClose={() => setConfirmDelete(null)}
            onConfirm={handleConfirmDelete}
            title={
              confirmDelete?.type === 'practice'
                ? 'Delete practice'
                : confirmDelete?.type === 'drill'
                  ? 'Delete drill'
                  : 'Remove position group'
            }
            message={
              confirmDelete?.type === 'practice'
                ? 'Are you sure you want to delete this practice? This action cannot be undone.'
                : confirmDelete?.type === 'drill'
                  ? confirmDelete.name
                    ? `Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`
                    : 'Are you sure you want to delete this drill? This action cannot be undone.'
                  : confirmDelete?.type === 'position_group'
                    ? `Are you sure you want to remove the "${confirmDelete.groupName}" position group? This will delete all steps and coaching points for this group.`
                    : ''
            }
            confirmLabel={confirmDelete?.type === 'position_group' ? 'Remove' : 'Delete'}
            confirmAriaLabel={confirmDelete?.type === 'practice' || confirmDelete?.type === 'drill' ? 'delete' : undefined}
            cancelLabel="Cancel"
            variant="danger"
            isConfirming={isConfirming}
          />
          
                              {/* Visual indicator for timers hidden as per client request */}
        </>
      )}
    </div>
  );
}

export default PracticeManagement;